import React, { useState, useEffect, useRef } from 'react';
import './VideoPlaylist.scss';
import YouTubeEmbed from './YouTubeEmbed.js';

function VideoPlaylist(props) {
  const [videos, setVideos] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [activeVideoId, setActiveVideoId] = useState('');
  const [resetYouTubeEmbed, setResetYouTubeEmbed] = useState(false);
  const [autoplay, setAutoplay] = useState(false);
  const mainVideoRef = useRef(null);
  const videoPlaceholdersRef = useRef(null);

  useEffect(() => {
    if (props?.fields?.VideoCategories) {
      setVideos(
        props.fields.VideoCategories.flatMap((category) =>
          category.fields.VideoList.map((video) => ({
            id: video.id,
            category: category.fields.CategoryName.value,
            title: video.fields.Title.value,
            youtubeId: video.fields.VideoLink.value.url.split('/')[4],
            thumbnail: video.fields.VideoThumbnail.value.url,
            description: video.fields.Description.value,
          }))
        )
      );

      setActiveCategory(props.fields.VideoCategories[0].fields.CategoryName.value);
      setActiveVideoId(props.fields.VideoCategories[0].fields.VideoList[0].id);
    }
  }, [props.fields.VideoCategories]);

  const handleCategoryClick = (category) => {
    dataLayer.push({
      event: 'user_guide_cta_click',
      cta_text: category,
      BrandName: 'TVS iQube',
      VariantName: 'SmartXonnect',
    });
    setActiveCategory(category);
    const firstVideoInCategory = videos.find((video) => video.category === category);
    setActiveVideoId(firstVideoInCategory.id);
    setResetYouTubeEmbed(true);
    setAutoplay(false);
  };

  const handleVideoClick = (videoId) => {
    setActiveVideoId(videoId);
    setResetYouTubeEmbed(false);
    setAutoplay(true);
  };

  const changeCategory = (direction) => {
    const categories = props.fields.VideoCategories.map((cat) => cat.fields.CategoryName.value);
    const currentIndex = categories.indexOf(activeCategory);
    let newIndex;

    if (direction === 'next') {
      newIndex = currentIndex + 1;
      if (newIndex >= categories.length) {
        newIndex = categories.length - 1; // Stay on the last category
      }
    } else {
      newIndex = currentIndex - 1;
      if (newIndex < 0) {
        newIndex = 0; // Stay on the first category
      }
    }

    handleCategoryClick(categories[newIndex]);
  };

  useEffect(() => {
    const mainVideo = mainVideoRef.current;
    if (mainVideo) {
      let touchStartX = 0;
      let touchEndX = 0;

      const handleTouchStart = (e) => {
        touchStartX = e.touches[0].clientX;
      };

      const handleTouchMove = (e) => {
        touchEndX = e.touches[0].clientX;
      };

      const handleTouchEnd = () => {
        if (touchStartX - touchEndX > 50) {
          changeCategory('next');
        } else if (touchEndX - touchStartX > 50) {
          changeCategory('prev');
        }
        touchStartX = 0;
        touchEndX = 0;
      };

      mainVideo.addEventListener('touchstart', handleTouchStart);
      mainVideo.addEventListener('touchmove', handleTouchMove);
      mainVideo.addEventListener('touchend', handleTouchEnd);

      return () => {
        mainVideo.removeEventListener('touchstart', handleTouchStart);
        mainVideo.removeEventListener('touchmove', handleTouchMove);
        mainVideo.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [activeCategory]);

  useEffect(() => {
    const container = videoPlaceholdersRef.current;
    const categoryVideos = videos.filter((video) => video.category === activeCategory);

    if (container && categoryVideos.length > 5 && window.innerWidth >= 769) {
      const scrollbarThumb = document.createElement('div');
      scrollbarThumb.className = 'custom-scrollbar-thumb';
      container.appendChild(scrollbarThumb);

      const updateScrollbarThumb = () => {
        const scrollPercentage =
          (container.scrollTop / (container.scrollHeight - container.clientHeight)) * 100;
        const thumbPosition =
          (scrollPercentage * (container.clientHeight - scrollbarThumb.clientHeight)) / 100;
        scrollbarThumb.style.transform = `translateY(${thumbPosition}px)`;
      };

      container.addEventListener('scroll', updateScrollbarThumb);
      updateScrollbarThumb(); // Initial position

      return () => {
        container.removeEventListener('scroll', updateScrollbarThumb);
        if (scrollbarThumb.parentNode === container) {
          container.removeChild(scrollbarThumb);
        }
      };
    }
  }, [activeCategory, videos]);

  const PlayButton = () => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g filter="url(#filter0_b_1781_8479)">
        <circle cx="13.8647" cy="13.8647" r="13.8647" fill="#333333" fillOpacity="0.8" />
        <circle
          cx="13.8647"
          cy="13.8647"
          r="13.5116"
          stroke="white"
          strokeOpacity="0.4"
          strokeWidth="0.706255"
        />
      </g>
      <path
        d="M11.2285 10.1655L17.8292 13.8619L11.2285 17.5583V10.1655Z"
        fill="white"
        stroke="white"
        strokeWidth="2.03082"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <filter
          id="filter0_b_1781_8479"
          x="-5.65004"
          y="-5.65004"
          width="39.0286"
          height="39.0296"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feGaussianBlur in="BackgroundImageFix" stdDeviation="2.82502" />
          <feComposite in2="SourceAlpha" operator="in" result="effect1_backgroundBlur_1781_8479" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_backgroundBlur_1781_8479"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );

  const activeVideo = videos.find((video) => video.id === activeVideoId);
  const categoryVideos = videos.filter((video) => video.category === activeCategory);

  // Check if there are more than 5 videos and screen is desktop size
  const isScrollable = categoryVideos.length > 5 && window.innerWidth >= 769;

  return (
    <div className="video-showcase" data-aos="fade-up">
      <div className="header">
        <p className="roboto-regular">{props?.fields?.Title?.value}</p>
        <h2 className="roboto-bold">{props?.fields?.SubTitle?.value}</h2>
      </div>

      <div className="category-container ">
        <ul className="nav nav-tabs">
          {props.fields.VideoCategories.map((category) => (
            <li className="nav-item" key={category.id}>
              <button
                className={`nav-link ${
                  activeCategory === category.fields.CategoryName.value ? 'active' : ''
                }`}
                onClick={() => handleCategoryClick(category.fields.CategoryName.value)}
              >
                {category.fields.CategoryName.value}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="main-video-container ">
        <div className="main-video" ref={mainVideoRef}>
          <YouTubeEmbed
            youtubeId={activeVideo?.youtubeId}
            thumbnail={activeVideo?.thumbnail}
            reset={resetYouTubeEmbed}
            autoplay={autoplay}
            title={activeVideo?.title}
            onResetComplete={() => setResetYouTubeEmbed(false)}
          />
          <div className="video-info">
            <h3 className="roboto-bold">{activeVideo?.title}</h3>
            <p className="roboto-regular ">{activeVideo?.description}</p>
          </div>
        </div>

        <h3 className="more-videos-heading roboto-medium ">More Videos</h3>

        <div
          ref={videoPlaceholdersRef}
          className={`video-placeholders ${isScrollable ? 'scrollable' : ''}`}
        >
          {categoryVideos
            .filter((video) => video.id !== activeVideoId)
            .map((video) => (
              <div
                key={video.id}
                className={`video-placeholder ${video.id === activeVideoId ? 'active' : ''}`}
                onClick={() => handleVideoClick(video.id)}
              >
                <div
                  className="thumbnail-container"
                  onClick={() => {
                    dataLayer.push({
                      event: 'user_guide_video_click',
                      cta_text: video.title,
                      section_name: activeCategory,
                      BrandName: 'TVS iQube',
                      VariantName: 'SmartXonnect',
                    });
                  }}
                >
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="img-fluid"
                    loading="lazy"
                  />
                  <div className="play-button-overlay">
                    <PlayButton />
                  </div>
                </div>
                <div className="video-info">
                  <span className="video-title">{video.title}</span>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export default React.memo(VideoPlaylist);
